import React, { useEffect, useRef, useState, memo, useMemo } from 'react';
import { Box, CloseButton, HStack, Text, IconButton, Tooltip } from '@chakra-ui/react';
import { Document, Page, pdfjs } from 'react-pdf';

interface CommonPdfPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  fileUrl: string;
  title?: string;
  initialPosition?: { x: number; y: number };
  initialSize?: { width: number; height: number };
}

// 默认初始位置和尺寸
const DEFAULT_SIZE = { width: 520, height: Math.round(window.innerHeight * 0.8) };
const DEFAULT_POSITION = { x: Math.max(8, window.innerWidth - DEFAULT_SIZE.width - 8), y: 120 };

// 仅设置一次 workerSrc，避免在频繁渲染时重置
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@4.8.69/build/pdf.worker.min.mjs`;

// 独立的 PDF 内容渲染子组件，避免拖拽时重复渲染 Document
const PdfContent: React.FC<{ fileUrl: string; width: number; options: any }> = memo(({ fileUrl, width, options }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    // 当文件变化时，清理错误状态
    setError('');
    setNumPages(null);
  }, [fileUrl]);

  return (
    <Box w={'100%'} h={'100%'}
      sx={{
        '.react-pdf__Document': {
          overflowY: 'auto',
          overflowX: 'hidden',
          height: '100%',
          width: '100%',
        },
        '.react-pdf__Page': {
          width: '100%',
          marginBottom: '1rem',
        },
        canvas: {
          width: '98%!important',
          border: '2px solid #D3D7E2',
          borderRadius: '12px',
        },
      }}
    >
      {error ? (
        <Box whiteSpace="pre-wrap" h="full" overflowY={'auto'}>{error}</Box>
      ) : (
        <Document
          key={fileUrl}
          file={fileUrl}
          options={options}
          onLoadSuccess={({ numPages }: { numPages: number }) => setNumPages(numPages)}
          onLoadError={(err) => setError(String(err))}
        >
          {Array.from(new Array(numPages || 0), (_, index) => (
            <Page
              key={`page_${index + 1}`}
              pageNumber={index + 1}
              width={width}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          ))}
        </Document>
      )}
    </Box>
  );
});

const CommonPdfPreview: React.FC<CommonPdfPreviewProps> = ({
  isOpen,
  onClose,
  fileUrl,
  title = 'Preview PDF',
  initialPosition,
  initialSize,
}) => {
  const pdfOptions = useMemo(() => ({
    cMapUrl: `//unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
  }), [pdfjs.version]);

  // 拖拽状态
  const [position, setPosition] = useState<{ x: number; y: number }>(initialPosition || DEFAULT_POSITION);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // 缩放状态
  const [size, setSize] = useState<{ width: number; height: number }>(initialSize || DEFAULT_SIZE);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [startSize, setStartSize] = useState<{ width: number; height: number }>(DEFAULT_SIZE);
  const [isMaximized, setIsMaximized] = useState(false);
  const [preMaxSize, setPreMaxSize] = useState<{ width: number; height: number } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  
  // 监听窗口大小变化，确保模态框始终在可见区域内（移植自 PdfReviewModal）
  useEffect(() => {
    const handleResize = () => {
      const padding = 8;
      const maxX = Math.max(padding, window.innerWidth - size.width - padding);
      setPosition(prev => ({
        x: Math.min(prev.x, maxX),
        y: prev.y,
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [size.width]);

  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      if (isDragging) {
        setPosition({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y });
      } else if (isResizing) {
        const dx = e.clientX - resizeStart.x;
        const dy = e.clientY - resizeStart.y;
        setSize({
          width: Math.min(Math.max(360, startSize.width + dx), Math.round(window.innerWidth * 0.95)),
          height: Math.min(Math.max(300, startSize.height + dy), Math.round(window.innerHeight * 0.95)),
        });
      }
    }

    function handleMouseUp() {
      setIsDragging(false);
      setIsResizing(false);
    }

    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset, resizeStart, startSize]);

  // 监听窗口大小变化，确保模态框始终在可见区域内（移植自 PdfReviewModal）
  useEffect(() => {
    const handleResize = () => {
      const padding = 8;
      const maxX = Math.max(padding, window.innerWidth - size.width - padding);
      setPosition(prev => ({
        x: Math.min(prev.x, maxX),
        y: Math.min(prev.y, Math.max(24, window.innerHeight - 80)),
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [size.width]);

  // 放大/缩小/最大化/还原
  const MIN_WIDTH = 360;
  const MIN_HEIGHT = 300;
  const MAX_WIDTH = Math.round(window.innerWidth * 0.95);
  const MAX_HEIGHT = Math.round(window.innerHeight * 0.95);

  const zoomIn = (step = 80) => {
    setSize(prev => ({
      width: Math.min(prev.width + step, MAX_WIDTH),
      height: Math.min(prev.height + Math.round(step * 0.6), MAX_HEIGHT),
    }));
  };

  const zoomOut = (step = 80) => {
    setSize(prev => ({
      width: Math.max(prev.width - step, MIN_WIDTH),
      height: Math.max(prev.height - Math.round(step * 0.6), MIN_HEIGHT),
    }));
  };

  const maximize = () => {
    if (!isMaximized) {
      setPreMaxSize(size);
      setIsMaximized(true);
      setPosition({ x: 8, y: 24 });
      setSize({ width: MAX_WIDTH, height: MAX_HEIGHT });
    }
  };

  const restore = () => {
    if (isMaximized) {
      setIsMaximized(false);
      setSize(preMaxSize || (initialSize || DEFAULT_SIZE));
      setPosition(initialPosition || DEFAULT_POSITION);
    }
  };

  const reset = () => {
    setIsMaximized(false);
    setSize(initialSize || DEFAULT_SIZE);
    setPosition(initialPosition || DEFAULT_POSITION);
  };

  // 键盘快捷键
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.ctrlKey && (e.key === '+' || e.key === '=')) {
        e.preventDefault();
        zoomIn();
      } else if (e.ctrlKey && (e.key === '-' || e.key === '_')) {
        e.preventDefault();
        zoomOut();
      } else if (e.ctrlKey && e.key === '0') {
        e.preventDefault();
        reset();
      } else if (e.key === 'Escape' && isMaximized) {
        e.preventDefault();
        restore();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, isMaximized, size]);

  if (!isOpen) return null;

  return (
    <Box
      ref={containerRef}
      position="fixed"
      left={`${position.x}px`}
      top={`${position.y}px`}
      width={`${size.width}px`}
      height={`${size.height}px`}
      bg="white"
      boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
      borderRadius="8px"
      zIndex={2000}
      overflow="hidden"
    >
      {/* Header 可拖拽 */}
      <Box
        as="header"
        p={4}
        borderBottom="1px solid #E2E8F0"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        cursor="move"
        onMouseDown={(e) => {
          // 仅当点击的是 header 本身时开始拖曳，避免与内部控件交互冲突（对齐 PdfReviewModal 行为）
          const headerEl = containerRef.current?.querySelector('header');
          if (headerEl && e.target !== headerEl) return;
          if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setIsDragging(true);
            setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
          }
        }}
        onDoubleClick={() => {
          isMaximized ? restore() : maximize();
        }}
      >
        <HStack>
          <Text fontWeight="bold">{title}</Text>
        </HStack>
        <HStack spacing={2}>
          <Tooltip label="缩小 (Ctrl -)" openDelay={300}><IconButton aria-label="缩小" size="sm" variant="ghost" onClick={() => zoomOut()} icon={<Box fontWeight="bold">−</Box>} /></Tooltip>
          <Tooltip label="放大 (Ctrl +)" openDelay={300}><IconButton aria-label="放大" size="sm" variant="ghost" onClick={() => zoomIn()} icon={<Box fontWeight="bold">＋</Box>} /></Tooltip>
          {isMaximized ? (
            <Tooltip label="还原" openDelay={300}><IconButton aria-label="还原" size="sm" variant="ghost" onClick={restore} icon={<Box fontWeight="bold">⤺</Box>} /></Tooltip>
          ) : (
            <Tooltip label="最大化" openDelay={300}><IconButton aria-label="最大化" size="sm" variant="ghost" onClick={maximize} icon={<Box fontWeight="bold">⤢</Box>} /></Tooltip>
          )}
          <Tooltip label="还原默认 (Ctrl 0)" openDelay={300}><IconButton aria-label="还原默认" size="sm" variant="ghost" onClick={reset} icon={<Box fontWeight="bold">⟲</Box>} /></Tooltip>
          <CloseButton
            _focusVisible={{ boxShadow: 'none' }}
            bg={'#FFFFFF'}
            width={'24px'}
            height={'24px'}
            _hover={{ bg: '#D3D7E2' }}
            color={'#8E9ABC'}
            fontSize={'8px'}
            borderRadius={'50%'}
            onClick={onClose}
          />
        </HStack>
      </Box>

      {/* Body */}
      <Box width={'full'} height={'calc(100% - 64px)'} p={4}>
        <PdfContent fileUrl={fileUrl} width={Math.max(360, size.width - 70)} options={pdfOptions} />
      </Box>

      {/* 右下角可见缩放手柄 */}
      <Tooltip label="拖拽以调整大小" openDelay={300} placement="top-start">
        <Box
          position="absolute"
          right={2}
          bottom={2}
          width="18px"
          height="18px"
          cursor="nwse-resize"
          borderRadius="2px"
          bg="transparent"
          _before={{
            content: '""',
            position: 'absolute',
            inset: 0,
            backgroundImage: 'repeating-linear-gradient(45deg, rgba(142,154,188,0.7) 0 2px, transparent 2px 4px)',
            opacity: 0.75,
          }}
          onMouseDown={(e) => {
            setIsResizing(true);
            setResizeStart({ x: e.clientX, y: e.clientY });
            setStartSize({ ...size });
          }}
        />
      </Tooltip>
    </Box>
  );
};

export default CommonPdfPreview;